<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { type FortuneWheelPrizeV2, type FortuneWheelSlice, WHEEL_CURRENCY } from '@/types/shared/fortune-wheel'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { usePlayer } from '@/composables/game/player'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { GameSound, useSounds } from '@/composables/game/sounds'
import { useAnalytics } from '@/composables/analytics'
import { twMerge } from 'tailwind-merge'
import GoldenWheelBackground from '@/assets/images/goldwheel-overlay-bg.webp'
import { useScreenSize } from '@/composables/helpers/screen-size'
import api from '@/services/api'
import webapp from '@/services/webapp'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'

const TREASURE_CHEST_ID = 'TREASURE_CHEST'

const props = withDefaults(defineProps<{
	isSpinning: boolean
	isGold?: boolean
	onlyVisual?: boolean
	initFinished?: boolean
	isAutoSpin?: boolean
	spinMultiplier: number
	isGiveawayActive?: boolean
}>(), {
  isGold: false,
  onlyVisual: false,
  initFinished: false,
  isAutoSpin: false,
  isGiveawayActive: false,
})
const emit = defineEmits<{
	(e: 'stopped', prize: FortuneWheelPrizeV2 | null, prizeMultiplier: number, isChest: boolean): void
}>()
const wheel = ref()
const square = ref<HTMLElement | null>(null)

const previousEndDegree = ref(0)

const { spin: _spin, checkOnboardingState, wheelSlices, goldenWheelSlices } = useFortuneWheel()
const { updatePlayer, goldenCoinValue } = usePlayer()
const { addHapticFeedback } = useHapticFeedback()
const { playSound, stopSound, preloadSounds } = useSounds()
const { triggerEvent } = useAnalytics()

preloadSounds([GameSound.WHEEL_SPINNING, GameSound.PURCHASE_SUCCESS, GameSound.BONUS_ACTIVATED])
const { width, height } = useScreenSize()

// Active slices based on wheel type
const slices = computed<FortuneWheelSlice[]>(() => props.isGold ? goldenWheelSlices.value : wheelSlices.value)
const rewardsCount = computed(() => slices.value.length)

const wheelStyle = computed(() => {
  return {
    '--_items': rewardsCount.value,
  }
})

const setStyle = ref<boolean>(false)

const squareStyle = computed(() => {
  if (!square.value || !setStyle.value || props.onlyVisual || !height.value) return {
    opacity: props.onlyVisual ? '1' : '0',
  }
  const squareHeight = square.value?.offsetHeight
  const maxWidth = width.value - 32
  if (squareHeight > maxWidth) {
    return {
      height: `${maxWidth}px`,
      width: `${maxWidth}px`,
      flex: 'unset',
      opacity: 1,
    }
  }
  return {
    height: `${squareHeight}px`,
    width: `${squareHeight}px`,
    flex: 'unset',
    opacity: 1,
  }
})

const currentPrize = ref<FortuneWheelPrizeV2 | null>(null)
const currentPrizeMultiplier = ref<number>(1)
const currentPrizeIsChest = ref<boolean>(false)
const finishedRotation = ref<boolean>(false)

const stopRotation = ref<boolean>(false)
const isFirstRotationFinished = ref<boolean>(false)
const finalAngleToRotate = ref<number>(0)
const sliceAngle = computed(() => 360 / rewardsCount.value)
const finalRounds = computed(() => props.isAutoSpin ? 2 : 4)

const startingSpeed = computed(() => props.isAutoSpin ? 30 : 20)
const extraSpins = computed(() => props.isAutoSpin ? 6 : 8)

function getItemStyle(index: number) {
  const mirroredAngle = 360 - (index * sliceAngle.value)
  return {
    '--_items': rewardsCount.value,
    '--_idx': index + 1,
    'align-content': 'center',
    'aspect-ratio': `1 / calc(2 * tan(180deg / ${rewardsCount.value}))`,
    'background': getColorBySlice(index),
    'clip-path': 'polygon(0% 0%, 100% 50%, 0% 100%)',
    'font-size': '5cqi',
    'grid-area': '1 / -1',
    'padding-left': '1ch',
    'rotate': `calc(${mirroredAngle}deg + 90deg)`,
    'transform-origin': 'center right',
    'width': '50cqi',
  }
}

// For golden wheel, alternate between two gold tones
const goldenWheelColors = ['#FFF246', '#E5B80B']

function getColorBySlice(index: number): string {
  const slice = slices.value[index]
  if (!slice) return '#FFFFFF'

  if (props.isGold) {
    return goldenWheelColors[index % 2]
  }

  // Use the color from backend config if available
  if (slice.color) {
    return slice.color
  }

  // Fallback to predefined colors (for backward compatibility)
  const predefinedCurrencyColorMap: Record<string, string> = {
    [WHEEL_CURRENCY.SPIN]: '#59B25C',
    [WHEEL_CURRENCY.STAR]: '#1A1E18',
    [WHEEL_CURRENCY.GOLDEN_COIN]: '#ECC542',
    [WHEEL_CURRENCY.DROPEE]: '#1A1E18',
    [WHEEL_CURRENCY.GIVEAWAY_TICKET]: '#706B35',
    [TREASURE_CHEST_ID]: '#5C62F9',
  }

  const SPECIAL_CURRENCY_DEFAULT_COLOR = '#32B8C4'
  const key = slice.prizeId === TREASURE_CHEST_ID ? TREASURE_CHEST_ID : slice.currency
  return predefinedCurrencyColorMap[key] ?? SPECIAL_CURRENCY_DEFAULT_COLOR
}

// Helper to calculate luminance and determine if text should be black (for light backgrounds)
function isLightColor(hexColor: string): boolean {
  // Remove # if present
  const hex = hexColor.replace('#', '')

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Calculate relative luminance (perceived brightness)
  // Formula: (0.299 * R + 0.587 * G + 0.114 * B)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // If luminance > 0.5, it's a light color
  return luminance > 0.5
}

// Determine if a slice should use black text (light background)
function isLightBackground(index: number): boolean {
  if (props.isGold) return true // All golden wheel slices have gold (light) background
  const slice = slices.value[index]
  if (!slice) return false

  // Use color-based detection if color is available from backend
  if (slice.color) {
    return isLightColor(slice.color)
  }

  // Fallback to currency-based detection
  return slice.currency === WHEEL_CURRENCY.GOLDEN_COIN
}

function getCoinsRewardClass(amount: string) {
  if (amount.length <= 2) return 'mb-3'
  if (amount.length === 3) return 'mb-1'
  if (amount.length === 4) return ''
  // if (amount.length === 5) return '-mb-1.5'
  // if (amount.length === 6) return '-mb-3'
  // if (amount.length === 7) return '-mb-[22px]'
  // if (amount.length === 8) return 'max-[375px]-mb-4 -mb-5 max-[375px]:!text-[10px] !text-xs'
  return ''
}

function rotateWheel({
  totalRounds = 4,
  targetAngle = 360,
  startingSpeed = 12,
  eventInterval = 45,
  minSpeed = 0.1,
  decelerationStartDistance = 0,
  onEvent = (angle: number) => console.log(`Event triggered at ${angle % 360} degrees`),
  onFinish,
}: {
	totalRounds: number, targetAngle: number,
	startingSpeed: number,
	eventInterval?: number,
	minSpeed?: number,
	decelerationStartDistance?: number,
	onEvent?: (angle: number) => void
	onFinish: () => void

}) {
  let animationFrameId = 0
  let currentAngle = 0
  let currentSpeed = startingSpeed
  let lastEventAngle = -eventInterval / 2

  const fullRotationDegrees = totalRounds * 360
  const finalTargetAngle = fullRotationDegrees + targetAngle

  function checkEvents(angle: number) {
    if (lastEventAngle + eventInterval < angle && currentSpeed <= 6) {
      lastEventAngle += eventInterval
      onEvent(lastEventAngle)
    } else if (currentSpeed > 6 && (lastEventAngle + 2 * eventInterval < angle)) {
      lastEventAngle += 2 * eventInterval
      onEvent(lastEventAngle)
    }
  }

  function calculateSpeed(remainingDistance: number) {
    const decelerationStart = decelerationStartDistance
    let speed

    if (remainingDistance <= decelerationStart) {
      const progress = remainingDistance / decelerationStart
      speed = Math.min(startingSpeed, Math.max(minSpeed, minSpeed + (startingSpeed - minSpeed) * progress))
    } else {
      speed = startingSpeed
    }
    return speed
  }

  function animate() {
    const remainingDistance = finalTargetAngle - currentAngle

    if (remainingDistance <= 0 || (stopRotation.value && currentAngle % 360 <= 0.1 && currentAngle > 360)) {
      currentAngle = finalTargetAngle
      wheel.value.style.transform = `rotate(${currentAngle}deg)`
      cancelAnimationFrame(animationFrameId)
      stopRotation.value = false
      onFinish()
      return
    }

    currentSpeed = calculateSpeed(remainingDistance)
    currentAngle += currentSpeed

    if (currentAngle > finalTargetAngle) {
      currentAngle = finalTargetAngle
    }

    wheel.value.style.transform = `rotate(${currentAngle}deg)`
    checkEvents(currentAngle)
    animationFrameId = requestAnimationFrame(animate)
  }

  animate()
}

async function doSpin() {
  addHapticFeedback()
  playSound(GameSound.WHEEL_SPINNING)
  triggerEvent({ event: 'spin_fortune_wheel' })
  stopRotation.value = false
  wheel.value.style.transition = ''
  wheel.value.style.transform = ''
  await new Promise(requestAnimationFrame)

  const startDegree = previousEndDegree.value % 360 + 180
  const initialSpinAngle = extraSpins.value * 360 + startDegree

  if (!props.isAutoSpin) {
    rotateWheel({
      totalRounds: extraSpins.value,
      targetAngle: 0,
      eventInterval: sliceAngle.value,
      startingSpeed: startingSpeed.value,
      onEvent: () => addHapticFeedback('light'),
      onFinish: () => isFirstRotationFinished.value = true,
    })
  } else {
    isFirstRotationFinished.value = true
  }

  // Snapshot slices BEFORE the API call — the spin response updates wheelSlicesState reactively,
  // so by the time getRewardFromAPI returns, slices.value may already have new values.
  // We must look up the prize in the slices that were visually rendered to the user.
  const slicesAtSpinTime = [...slices.value]

  const { prize: reward, prizeMultiplier } = await getRewardFromAPI({ spinMultiplier: props.spinMultiplier })
  currentPrizeMultiplier.value = prizeMultiplier

  if (!reward) {
    emit('stopped', null, 1, false)
    return
  }

  // Find slice index: match prizeId, fall back to TREASURE_CHEST
  const namedSliceIndex = slicesAtSpinTime.findIndex(s => s.prizeId === reward.id && s.prizeId !== TREASURE_CHEST_ID)
  const chestSliceIndex = slicesAtSpinTime.findIndex(s => s.prizeId === TREASURE_CHEST_ID)

  let rewardIndex: number
  let isChest: boolean

  if (namedSliceIndex !== -1) {
    rewardIndex = namedSliceIndex
    isChest = false
  } else if (chestSliceIndex !== -1) {
    rewardIndex = chestSliceIndex
    isChest = true
  } else {
    // No matching slice and no chest — report error and bail
    await webapp.showAlert('Your prize has been added but we failed to show it. Please refresh the app.')
    await api.reportError({
      code: 'prizeNotFound',
      description: `V2 prize not found on the${props.isGold ? ' golden' : ''} wheel`,
      metadata: reward,
    })
    emit('stopped', null, 1, false)
    return
  }

  currentPrize.value = reward
  currentPrizeIsChest.value = isChest

  const targetAngle = rewardIndex * sliceAngle.value
  finalAngleToRotate.value = finalRounds.value * 360 + startDegree + targetAngle - (initialSpinAngle % 360 + 180)
  previousEndDegree.value = finalAngleToRotate.value % 360
  stopRotation.value = true
}

async function getRewardFromAPI({ spinMultiplier }: { spinMultiplier: number }): Promise<{
	prize: FortuneWheelPrizeV2 | null,
	prizeMultiplier: number
}> {
  const result = await _spin({ multiplier: spinMultiplier })
  if (result) {
    updatePlayer(result.playerStats)
    checkOnboardingState().then()
    return { prize: result.prize, prizeMultiplier: spinMultiplier }
  }
  return { prize: null, prizeMultiplier: 1 }
}

watch([finalAngleToRotate, isFirstRotationFinished], () => {
  if (finalAngleToRotate.value && isFirstRotationFinished.value) {
    if (!props.isAutoSpin) {
      rotateWheel({
        totalRounds: finalRounds.value,
        targetAngle: (finalAngleToRotate.value % 360),
        startingSpeed: startingSpeed.value,
        eventInterval: sliceAngle.value,
        minSpeed: 0.1,
        decelerationStartDistance: (finalRounds.value / 2) * 360 + (2 * Math.random() * 100),
        onEvent: () => addHapticFeedback('light'),
        onFinish: () => finishedRotation.value = true,
      })
    } else {
      finishedRotation.value = true
    }

    finalAngleToRotate.value = 0
    isFirstRotationFinished.value = false
  }
})

watch([finishedRotation, currentPrize], () => {
  if (finishedRotation.value && currentPrize.value) {
    stopSound(GameSound.WHEEL_SPINNING)
    const isBigWin = currentPrize.value.id === 'goldenCoin_0.4'
    playSound(props.isAutoSpin && isBigWin ? GameSound.BONUS_ACTIVATED : GameSound.PURCHASE_SUCCESS)
    emit('stopped', currentPrize.value, currentPrizeMultiplier.value, currentPrizeIsChest.value)
  }
})

watch(() => props.isSpinning, (isSpinning) => {
  if (isSpinning) {
    currentPrize.value = null
    currentPrizeIsChest.value = false
    finishedRotation.value = false
    doSpin()
  }
})

watch([square, () => props.initFinished], () => {
  if (!square.value || !props.initFinished) return
  nextTick(() => {
    setStyle.value = true
  })
}, { immediate: true })

watch(() => props.isGold, () => {
  setStyle.value = false
  setTimeout(() => setStyle.value = true, 400)
})

watch([width, height], () => {
  nextTick(() => {
    setStyle.value = false
    setTimeout(() => {
      setStyle.value = true
    }, 100)
  })
})
</script>
<template>
  <div
    class="relative flex flex-1 flex-col items-center max-w-full"
    :class="{'z-[92]': isAutoSpin}"
  >
    <Transition name="fade-fast">
      <img
        v-if="isGold && !onlyVisual && setStyle"
        :src="GoldenWheelBackground"
        class="absolute top-[calc(50%-40px)] left-1/2 -translate-x-1/2 -translate-y-1/2 h-[calc(2*100%)] w-auto max-w-none"
      >
    </Transition>
    <div
      ref="square"
      class="max-w-full relative flex-1 transition-[opacity] duration-150"
      :class="[!setStyle && !props.onlyVisual && 'invisible', props.onlyVisual && 'w-full h-full flex items-center justify-center']"
      :style="squareStyle"
    >
      <div
        :class="twMerge('bg-[#FFCA45] rounded-full w-full max-[400px]:p-1.5 p-2 z-[10] min-w-[280px]', onlyVisual && 'only-visual', isSpinning && 'z-[92]')"
        style="box-shadow: 0 0 20.716px 0 rgba(0, 0, 0, 0.66) inset, 0 0 45.851px 0 #000;"
      >
        <div
          v-if="slices.length"
          :class="twMerge('ui-wheel-of-fortune', isGold && 'gold', props.onlyVisual && '!max-w-none', isAutoSpin && 'infinite-rotate')"
          :style="wheelStyle"
        >
          <ul
            ref="wheel"
          >
            <li
              v-for="(slice, index) in slices"
              :key="index"
              :class="twMerge('flex items-center relative', isGold && 'gold', isLightBackground(index) && 'black')"
              :style="getItemStyle(index)"
            >
              <div class="inset-0 absolute flex items-center">
                <!-- Treasure Chest -->
                <span
                  v-if="slice.prizeId === 'TREASURE_CHEST'"
                  class="rotate-90 flex items-center justify-center relative w-[73px] -ml-2 min-[400px]:-ml-3"
                >
                  <img
                    class="w-[26px] min-[401]:w-[32px] h-auto object-cover z-[1]"
                    src="@/assets/images/chest.png"
                  >
                  <img
                    src="@/assets/images/goldwheel-overlay-bg.webp"
                    class="w-[60px] min-[401]:w-[68px] h-auto object-cover absolute"
                  >
                </span>

                <!-- Spin -->
                <span
                  v-else-if="slice.currency === 'spin'"
                  class="rotate-90 flex flex-col items-center gap-1 ml-4"
                >
                  <img
                    :class="twMerge('max-[400px]:size-5 max-[400px]:min-w-[20px] size-6 min-w-[24px]', onlyVisual && 'max-[400px]:size-[38px] max-[400px]:min-w-[38px] size-[42px] min-w-[42px]')"
                    src="@/assets/images/fortune-wheel.webp"
                  >
                  <span
                    class="text-black"
                    :class="onlyVisual && '!text-[26px] !leading-[34px]'"
                  >{{ slice.amount }}</span>
                </span>

                <!-- Star -->
                <span
                  v-else-if="slice.currency === 'star'"
                  class="rotate-90 flex flex-col items-center gap-1 ml-4"
                >
                  <img
                    :class="twMerge('max-[400px]:size-5 max-[400px]:min-w-[20px] size-6 min-w-[24px]', onlyVisual && 'max-[400px]:size-[38px] max-[400px]:min-w-[38px] size-[42px] min-w-[42px]')"
                    src="@/assets/images/premium.png"
                  >
                  <span
                    :class="onlyVisual && '!text-[26px] !leading-[34px]'"
                  >{{ slice.amount }}</span>
                </span>

                <!-- Golden Coin -->
                <NumberFormatTooltip
                  v-else-if="slice.currency === 'goldenCoin'"
                  :number="slice.amount * goldenCoinValue"
                  :formatting-type="'full'"
                >
                  <span
                    class="rotate-90 flex flex-col items-center ml-2"
                  >
                    <img
                      :src="CoinIcon"
                      class="size-6"
                      alt="Coin Icon"
                    >
                    <span
                      :class="getCoinsRewardClass(formatNumberFullTruncate(slice.amount * goldenCoinValue))"
                    >
                      {{ formatNumberFullTruncate(slice.amount * goldenCoinValue) }}
                    </span>
                  </span>
                </NumberFormatTooltip>

                <!-- Dropee -->
                <span
                  v-else-if="slice.currency === 'dropee'"
                  class="rotate-90 flex flex-col items-center gap-1 ml-4"
                >
                  <img
                    :class="twMerge('max-[400px]:size-5 max-[400px]:min-w-[20px] size-6 min-w-[24px]', onlyVisual && 'max-[400px]:size-[38px] max-[400px]:min-w-[38px] size-[42px] min-w-[42px]')"
                    src="@/assets/images/dropee-token-new.webp"
                  >
                  <span
                    :class="[onlyVisual ? '!text-[26px] !leading-[34px]' : '', getCoinsRewardClass(formatNumberFullTruncate((slice.amount / 100).toString()))]"
                  >{{ slice.amount / 100 }}</span>
                </span>

                <!-- Giveaway Ticket -->
                <span
                  v-else-if="slice.currency === WHEEL_CURRENCY.GIVEAWAY_TICKET"
                  class="rotate-90 flex items-center justify-center relative w-[73px] -ml-2 min-[400px]:-ml-3"
                >
                  <img
                    class="w-[26px] min-[401]:w-[32px] h-auto object-cover z-[1]"
                    src="@/assets/images/emojis/emoji-ticket.png"
                  >
                  <img
                    src="@/assets/images/goldwheel-overlay-bg.webp"
                    class="w-[60px] min-[401]:w-[68px] h-auto object-cover absolute"
                  >
                </span>

                <!-- Special currency from allSpecialCurrenciesConfig (live ops + hardcoded special currencies) -->
                <span
                  v-else
                  class="rotate-90 flex flex-col items-center gap-1 ml-2"
                >
                  <img
                    v-if="allSpecialCurrenciesConfig[slice.currency]"
                    class="w-[34px] min-w-[34px] object-cover"
                    :src="allSpecialCurrenciesConfig[slice.currency].image"
                  >
                  <span :class="getCoinsRewardClass(formatNumberFullTruncate(slice.amount))">
                    {{ slice.amount }}
                  </span>
                </span>
              </div>
            </li>
          </ul>
        </div>
        <img
          src="@/assets/images/wheel-arrow-big.png"
          :class="twMerge('absolute object-contain bottom-0 translate-y-1/2 left-1/2 -translate-x-1/2 h-8 min-[380px]:h-[46px]', onlyVisual && 'h-[54px]')"
        >
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.ui-wheel-of-fortune {
	all: unset;
	container-type: inline-size;
	direction: ltr;
	display: grid;
	position: relative;

	max-width: 100%;
	width: auto;
	max-height: 100%;
	aspect-ratio: 1 / 1;

	&.gold {
		@apply w-full;
	}

	&.only-visual {
		width: 100%;
		max-width: unset;
	}

	@apply flex items-center justify-center;

	& > * {
		position: absolute;
	}

	ul {
		all: unset;
		clip-path: inset(0 0 0 0 round 50%);
		display: grid;
		inset: 0;
		place-content: center start;
		width: 100%;
		height: 100%;
		border-radius: 50%;
		overflow: hidden;
		aspect-ratio: 1/1;

		li {
			span {
				@apply text-center text-white font-bold -tracking-[0.32px] max-[400px]:text-xs text-sm;
			}
		}

		li.gold {
			span {
				@apply text-black
			}
		}

		li.black {
			span {
				@apply text-black
			}
		}
	}
}

@keyframes rotating {
	0% {
		transform: rotate(0);
	}
	100% {
		transform: rotate(360deg);
	}
}

.infinite-rotate {
	animation: rotating 0.3s linear infinite;
}
</style>
